import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';

import { sessionAtom } from './stores/auth.atom';
import { env } from './lib/env';
import { RouteLoader } from './components/route-loader';
import { useRouteTitle } from './lib/page-title';
import { loginPathFor } from './lib/auth-redirect';
import { useKitScope } from './lib/kit-scope';
import { useThemeScope } from './lib/theme';
import { can, useCurrentOrg } from './lib/permissions';
import { panelChunks } from './lib/panel-chunks';

// Public pages — eagerly loaded (small)
const TourViewerPage = lazy(() => import('./pages/public/tour-viewer'));
const OrgPublicPage = lazy(() => import('./pages/public/org-page'));
const TourEmbedPage = lazy(() => import('./pages/public/tour-embed'));
const NotFoundPage = lazy(() => import('./pages/not-found'));

// Auth pages
const LoginPage = lazy(() => import('./pages/auth/login'));
const SignupPage = lazy(() => import('./pages/auth/signup'));
const OAuthCallbackPage = lazy(() => import('./pages/auth/oauth-callback'));
const AcceptInvitePage = lazy(() => import('./pages/auth/accept-invite'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/forgot-password'));
const ResetPasswordPage = lazy(() => import('./pages/auth/reset-password'));

// Integration deep-link landing (does its own session gate)
const ConnectPage = lazy(() => import('./pages/connect'));

// Admin pages (their loaders live in lib/panel-chunks, shared with the preloads)
const AdminLayout = lazy(() => import('./pages/admin/layout'));
const AdminDashboard = lazy(panelChunks.dashboard);
const AdminNotFound = lazy(panelChunks.notFound);
const HelpIndexPage = lazy(panelChunks.helpIndex);
const HelpArticlePage = lazy(panelChunks.helpArticle);
const AccountPage = lazy(panelChunks.account);
const NewOrganizationPage = lazy(panelChunks.newOrganization);
const OrgDashboard = lazy(panelChunks.orgHome);
const OrgMembers = lazy(panelChunks.members);
const OrgSettings = lazy(panelChunks.settings);
const OrgBilling = lazy(panelChunks.billing);
const OrgStats = lazy(panelChunks.stats);
const ProjectDashboard = lazy(panelChunks.project);
const ProjectEdit = lazy(panelChunks.projectEdit);
const TourEdit = lazy(panelChunks.tour);
const PanoramaEditor = lazy(panelChunks.panoramaEditor);

// Dev-only visual harness (never in production builds): renders editor panels
// in isolation so their layout can be screenshotted without logging in.
const DesignPanelPreview = import.meta.env.DEV ? lazy(() => import('./pages/dev/design-panel-preview')) : null;
const MenuPreview = import.meta.env.DEV ? lazy(() => import('./pages/dev/menu-preview')) : null;
const TourTabsPreview = import.meta.env.DEV ? lazy(() => import('./pages/dev/tour-tabs-preview')) : null;
const ConnectionsPreview = import.meta.env.DEV ? lazy(() => import('./pages/dev/connections-preview')) : null;
const MarkerPanelPreview = import.meta.env.DEV ? lazy(() => import('./pages/dev/marker-panel-preview')) : null;

/** Exported for tests only; the router is its single consumer. */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const session = useAtomValue(sessionAtom);
  const location = useLocation();
  // `undefined` = still hydrating Supabase session from localStorage; render
  // a loader instead of redirecting to /login (refresh-bounce-to-login bug).
  if (session === undefined) return <RouteLoader />;
  // Back to this same page after signing in (`?redirect=`, see auth-redirect).
  if (session === null) return <Navigate to={loginPathFor(location)} replace />;
  return <>{children}</>;
}

export default function AppRouter() {
  // En embed el iframe no tiene pestaña propia que titular.
  useRouteTitle(!env.IS_EMBED_MODE);
  // New kit on the tool routes; the visitor routes keep their own look.
  useKitScope(env.IS_EMBED_MODE);
  // Light / dark of the tool routes (the top bar's toggle); never on the visitor ones.
  useThemeScope(env.IS_EMBED_MODE);

  // Embed mode: only public viewer routes
  if (env.IS_EMBED_MODE) {
    return (
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/:publicId" element={<TourEmbedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        {DesignPanelPreview && <Route path="/dev/design-panel" element={<DesignPanelPreview />} />}
        {MenuPreview && <Route path="/dev/menu" element={<MenuPreview />} />}
        {TourTabsPreview && <Route path="/dev/tour-tabs" element={<TourTabsPreview />} />}
        {ConnectionsPreview && <Route path="/dev/connections" element={<ConnectionsPreview />} />}
        {MarkerPanelPreview && <Route path="/dev/marker-panel" element={<MarkerPanelPreview />} />}

        {/* Public viewer */}
        <Route path="/t/:publicId" element={<TourViewerPage />} />
        <Route path="/o/:slug" element={<OrgPublicPage />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        <Route path="/accept-invite" element={<LegacyAcceptInvite />} />
        {/* Password recovery. `/reset-password` is intentionally NOT behind
            ProtectedRoute: it has to render its own "link expired" state, and
            a redirect to /login would strand anyone arriving with a dead link. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* urbania3d integration deep-link */}
        <Route path="/connect" element={<ConnectPage />} />

        {/* Admin (protected) */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="help" element={<HelpIndexPage />} />
          <Route path="help/:slug" element={<HelpArticlePage />} />
          <Route path="organizations/new" element={<NewOrganizationPage />} />
          <Route path="organizations/:orgSlug">
            <Route index element={<OrgDashboard />} />
            <Route path="members" element={<OrgMembers />} />
            <Route path="settings" element={<OrgSettings />} />
            <Route path="billing" element={<OrgBilling />} />
            <Route path="stats" element={<OrgStats />} />
            <Route path="projects">
              {/* There is no projects list any more: the sidebar lists them. */}
              <Route index element={<Navigate to=".." replace />} />
              <Route path=":projectSlug">
                <Route index element={<ProjectDashboard />} />
                <Route path="edit" element={<ProjectEdit />} />
                <Route path="tours/:tourSlug" element={<TourEdit />} />
                <Route
                  path="tours/:tourSlug/panoramas/:panoramaId"
                  element={
                    <EditorAccessGate>
                      <PanoramaEditor />
                    </EditorAccessGate>
                  }
                />
              </Route>
            </Route>
            {/* Org-scoped catch-all, declared INSIDE `organizations/:orgSlug`
                so `orgSlug` still parses. That keeps the sidebar rendered and
                lets the panel offer "back to this org" instead of dumping the
                user at the account root. */}
            <Route path="*" element={<AdminNotFound />} />
          </Route>
          {/* Admin-wide catch-all. Without these an unknown path under
              `/admin/*` still matched AdminLayout, found no child route, and
              rendered an empty <Outlet /> — a blank page with only the nav
              bar. The top-level `*` never gets a chance to fire for them. */}
          <Route path="*" element={<AdminNotFound />} />
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

/**
 * The panorama editor is for members who can change markers or panoramas; anyone
 * else who lands on its URL goes back to the tour page (which has the visitor preview).
 */
function EditorAccessGate({ children }: { children: React.ReactNode }) {
  const { orgSlug, projectSlug, tourSlug } = useParams<{ orgSlug: string; projectSlug: string; tourSlug: string }>();
  const org = useCurrentOrg();
  if (org && !can(org, 'markers.edit') && !can(org, 'panoramas.edit')) {
    return <Navigate to={`/admin/organizations/${orgSlug}/projects/${projectSlug}/tours/${tourSlug}`} replace />;
  }
  return <>{children}</>;
}

/** Invitation emails sent before 2026-10-26 linked to `/accept-invite?token=…`. */
function LegacyAcceptInvite() {
  const token = new URLSearchParams(useLocation().search).get('token');
  return <Navigate to={token ? `/accept-invite/${encodeURIComponent(token)}` : '/login'} replace />;
}
